import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import api from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Users, Ticket, Package, DollarSign, Activity, TrendingUp, AlertTriangle } from 'lucide-react';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { formatInCST } from '../utils/timezone';
import { RecentActivityPanel } from '../components/RecentActivityPanel';

export default function Dashboard() {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [recentActivity, setRecentActivity] = useState([]);
  const [slaAlerts, setSlaAlerts] = useState({ total: 0, response_breach: 0, resolution_breach: 0 });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadDashboardData();
  }, []);

  const loadDashboardData = async () => {
    try {
      const [statsRes, activityRes, slaRes] = await Promise.all([
        api.get('/dashboard/stats'),
        api.get('/dashboard/recent-activity'),
        api.get('/sla-alerts/summary').catch(() => ({ data: { total: 0, response_breach: 0, resolution_breach: 0 } })),
      ]);
      setStats(statsRes.data);
      setRecentActivity(activityRes.data);
      setSlaAlerts(slaRes.data);
    } catch (error) {
      toast.error('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  const statCards = [
    {
      title: 'Total Customers',
      value: stats?.customers || 0,
      icon: Users,
      color: '',
      bgColor: 'co-ico-blue',
      link: '/customers'
    },
    {
      title: 'Open Tickets',
      value: stats?.tickets_open || 0,
      icon: Ticket,
      color: '',
      bgColor: 'co-ico-orange',
      link: '/tickets'
    },
    {
      title: 'Active Assets',
      value: stats?.assets || 0,
      icon: Package,
      color: '',
      bgColor: 'co-ico-green',
      link: '/assets'
    },
    {
      title: 'Unpaid Invoices',
      value: `$${(stats?.unpaid_total || 0).toFixed(2)}`,
      icon: DollarSign,
      color: '',
      bgColor: 'co-ico-red',
      link: '/invoices'
    }
  ];

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center h-screen">
        <img 
          src="/cipherone-logo.png" 
          alt="Loading" 
          className="h-16 w-16 logo-spin mb-4"
        />
        <div className="text-lg text-muted-foreground">Loading dashboard...</div>
      </div>
    );
  }

  return (
    <div className="co-page co-scope space-y-4 sm:space-y-6" data-testid="dashboard-page">
      <div>
        <h1 className="text-2xl sm:text-3xl font-bold">Dashboard</h1>
        <p className="text-muted-foreground text-sm sm:text-base mt-1">Welcome back, {user?.full_name || user?.email}!</p>
      </div>

      {/* Ticket Workbench - service-desk-first: ticket visibility above generic metrics */}
      <Card data-testid="ticket-workbench-card">
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2">
            <Ticket className="h-5 w-5 text-[#37c9ee]" />
            Ticket Workbench
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-col sm:flex-row sm:items-center gap-3">
            <Link
              to="/tickets?new=1"
              data-testid="workbench-new-ticket"
              className="inline-flex items-center justify-center gap-2 rounded-md text-sm font-semibold text-[#041019] bg-[#37c9ee] hover:brightness-110 transition px-4"
              style={{ minHeight: '44px' }}
            >
              New Ticket
            </Link>
            <Link
              to="/tickets?status=open,in_progress"
              data-testid="workbench-open-tickets"
              className="inline-flex items-center justify-center gap-2 rounded-md text-sm font-medium border border-[#193b4b] bg-white/5 hover:bg-white/10 transition px-4"
              style={{ minHeight: '44px' }}
            >
              Open Tickets
              <Badge variant="outline" className="text-xs font-normal">{stats?.tickets_open || 0}</Badge>
            </Link>
            <Link
              to="/tickets"
              data-testid="workbench-all-tickets"
              className="inline-flex items-center justify-center gap-2 rounded-md text-sm font-medium border border-[#193b4b] bg-white/5 hover:bg-white/10 transition px-4"
              style={{ minHeight: '44px' }}
            >
              View All Tickets
            </Link>
          </div>
        </CardContent>
      </Card>

      <div className="grid grid-cols-2 md:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-6">
        {statCards.map((stat, index) => (
          <Link key={index} to={stat.link} data-testid={`stat-card-${index}`}>
            <Card className="hover:shadow-lg transition-shadow cursor-pointer">
              <CardHeader className="flex flex-row items-center justify-between pb-2">
                <CardTitle className="text-sm font-medium text-muted-foreground">
                  {stat.title}
                </CardTitle>
                <div className={`p-2 rounded-lg ${stat.bgColor}`}>
                  <stat.icon className={`h-5 w-5 ${stat.color}`} />
                </div>
              </CardHeader>
              <CardContent>
                <div className="text-xl sm:text-3xl font-bold">{stat.value}</div>
              </CardContent>
            </Card>
          </Link>
        ))}
      </div>

      {/* SLA Breach Alert Banner */}
      {slaAlerts.total > 0 && (
        <Card className="border-red-500/40 bg-red-500/10" data-testid="sla-breach-card">
          <CardContent className="py-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-red-500/15">
                <AlertTriangle className="h-5 w-5 text-red-300" />
              </div>
              <div className="flex-1">
                <h3 className="font-semibold text-red-200">SLA Breach Alerts</h3>
                <p className="text-sm text-red-300">
                  {slaAlerts.total} active {slaAlerts.total === 1 ? 'alert' : 'alerts'}
                  {slaAlerts.response_breach > 0 && <> — {slaAlerts.response_breach} response {slaAlerts.response_breach === 1 ? 'breach' : 'breaches'}</>}
                  {slaAlerts.resolution_breach > 0 && <> — {slaAlerts.resolution_breach} resolution {slaAlerts.resolution_breach === 1 ? 'breach' : 'breaches'}</>}
                </p>
              </div>
              <Link to="/sla-alerts" className="text-sm text-red-300 font-medium hover:underline">View Alerts</Link>
            </div>
          </CardContent>
        </Card>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Activity className="h-5 w-5" />
              Recent Tickets
            </CardTitle>
          </CardHeader>
          <CardContent>
            {recentActivity.length === 0 ? (
              <p className="text-muted-foreground text-center py-4">No recent tickets</p>
            ) : (
              <div className="space-y-3">
                {recentActivity.slice(0, 5).map((activity, index) => {
                  const link = activity.type === 'ticket' 
                    ? `/tickets/${activity.id || activity.ticket_id}` 
                    : `/customers/${activity.id || activity.customer_id}`;
                  
                  return (
                    <Link 
                      key={index} 
                      to={link}
                      className="flex items-start gap-3 p-3 co-soft rounded-lg hover:shadow-md transition-all cursor-pointer"
                    >
                      <div className={`p-2 rounded-lg ${
                        activity.type === 'ticket' ? 'co-ico-orange' : 'co-ico-blue'
                      }`}>
                        {activity.type === 'ticket' ? (
                          <Ticket className="h-4 w-4" />
                        ) : (
                          <Users className="h-4 w-4" />
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium truncate">
                          {activity.type === 'ticket' ? activity.title : activity.company_name}
                        </p>
                        <p className="text-xs text-muted-foreground">
                          {activity.type === 'ticket' ? 'Ticket' : 'Customer'} •{' '}
                          {formatInCST(activity.created_at, 'MMM d, yyyy')}
                        </p>
                      </div>
                    </Link>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <TrendingUp className="h-5 w-5" />
              Quick Stats
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <Link 
                to="/tickets" 
                className="flex justify-between items-center p-2 rounded hover:bg-white/5 transition-colors cursor-pointer"
              >
                <span className="text-sm text-muted-foreground">Total Tickets</span>
                <span className="text-lg font-semibold">{stats?.tickets_total || 0}</span>
              </Link>
              <Link 
                to="/invoices" 
                className="flex justify-between items-center p-2 rounded hover:bg-white/5 transition-colors cursor-pointer"
              >
                <span className="text-sm text-muted-foreground">Unpaid Invoices Count</span>
                <span className="text-lg font-semibold">{stats?.unpaid_invoices || 0}</span>
              </Link>
              <Link 
                to="/customers" 
                className="flex justify-between items-center p-2 rounded hover:bg-white/5 transition-colors cursor-pointer"
              >
                <span className="text-sm text-muted-foreground">Active Customers</span>
                <span className="text-lg font-semibold">{stats?.customers || 0}</span>
              </Link>
              <Link 
                to="/assets" 
                className="flex justify-between items-center p-2 rounded hover:bg-white/5 transition-colors cursor-pointer"
              >
                <span className="text-sm text-muted-foreground">Active Assets</span>
                <span className="text-lg font-semibold">{stats?.assets || 0}</span>
              </Link>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Unified Recent Activity feed: portal auth, Ninja device/app events, remote commands */}
      <RecentActivityPanel />
    </div>
  );
}
